import { computed } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import store from '@/store'

/**
 * Factory defaults for the Analyze page's chart telemetries. One entry per channel:
 *   active   — selected out of the box
 *   group    — heading it sits under in the settings modal (`analisys.chart_groups.<group>`)
 *   chartUm  — unit of measure appended to the displayed value
 *   count    — occupies a chart Y axis, so it counts towards the 4-series limit
 *
 * Chart-only positioning knobs, read by `ActivityChart.buildYAxis`. In practice only
 * `activity` carries them — they are what push the driver-activity band to the bottom:
 *   top, height, opposite, format
 *
 * ORDER IS SIGNIFICANT. `ActivityChart.computedYAxis` derives each series' Y-axis index
 * and colour from its position here, and the settings modal renders the group headings in
 * first-encounter order. Reordering these keys visibly changes the chart.
 */
const CHART_DEFAULTS = {
    activity: {
        active: true,
        top: '79%',
        height: '25%',
        opposite: true,
        group: 'driver_data',
        count: false,
        extension: {
            computed: false
        }
    },
    speed: {
        active: true,
        group: 'movement_data',
        chartUm: 'km/h',
        count: true
    },
    tacho_speed: {
        active: false,
        group: 'movement_data',
        chartUm: 'km/h',
        count: true
    },
    odometer: {
        active: false,
        group: 'movement_data',
        chartUm: 'Km',
        count: true
    },
    gps_odometer: {
        active: false,
        group: 'movement_data',
        chartUm: 'Km',
        count: true
    },
    altitude: {
        active: true,
        group: 'movement_data',
        chartUm: 'm s.l.m.',
        count: true
    },
    lastpoint_weight: {
        active: false,
        group: 'movement_data',
        chartUm: 'Kg',
        count: true
    },
    engine_rpm: {
        active: false,
        group: 'engine_data',
        chartUm: 'RPM',
        count: true
    },
    engine_coolant_temperature: {
        active: false,
        group: 'engine_data',
        chartUm: '°C',
        count: true
    },
    consumption: {
        active: true,
        format: '{value} l',
        group: 'engine_data',
        chartUm: 'l'
    }
}

/**
 * What the map section ADDS TO or OVERRIDES on the chart defaults — everything here is a
 * value ACCUMULATED from the start of the route up to the clicked point, which is what the
 * `accumulated_data` group's ⓘ hint explains.
 *
 * `distance` is an addition: it is not a channel read off a route point, so it is
 * meaningless as a chart series and stays out of `CHART_DEFAULTS`. `consumption` is an
 * override: the chart plots it too, so it lives in `CHART_DEFAULTS`, but on the map it is
 * the running fuel total (`MapRouteAnalizer.drawRoute` diffs `fuel_total` from the route
 * start) and belongs in this group rather than under `engine_data`.
 */
const ACCUMULATED_DEFAULTS = {
    // Km travelled from the start of the route up to the clicked point.
    distance: {
        active: false,
        group: 'accumulated_data',
        chartUm: 'Km',
        count: false
    },
    // Litres burnt from the start of the route up to the clicked point. Same key as the
    // chart's entry, so saved selections carry over untouched; the spread below replaces
    // the whole entry for the map only. Dropping `format` is deliberate — it is read by
    // `ActivityChart.buildYAxis` for the chart section, never by the map.
    consumption: {
        active: true,
        group: 'accumulated_data',
        chartUm: 'l',
        count: false
    }
}

/**
 * The two independent sections of the Analyze page config, each persisted under its own
 * `config.user` key, mapped to the defaults that back it.
 */
const SECTION_DEFAULTS = {
    chart: CHART_DEFAULTS,
    mapTelemetries: { ...CHART_DEFAULTS, ...ACCUMULATED_DEFAULTS }
}

/**
 * Reads one section of the Analyze page config.
 *
 * `defaults`  — every field with its factory value; use it to reset to factory settings.
 * `selection` — the live selection: the stored flags merged over the defaults.
 *
 * The merge is the whole point. Only `{active, extension}` is persisted, so the raw
 * stored blob carries no `group`/`chartUm` — a consumer reading it directly loses units
 * and grouping.
 *
 * Reads `store.state.config.user` raw rather than going through the `config/value`
 * getter: the schema default for these keys is documentary only (see
 * `docs/architecture/config-bus.md`), so an absent key must fall through to these
 * defaults rather than to the schema's `{}`.
 *
 * A section name IS its `config.user` key, which is why these two kept their historical
 * names: renaming them would orphan every user's saved selection.
 *
 * @param {'chart'|'mapTelemetries'} section
 */
export function useAnalyzePageConfig(section) {
    // Deep clone, mirroring `defaultOf()` in `config/configSchema.js`: the section objects
    // are module-level singletons, and `mapTelemetries` is a SHALLOW spread of
    // `CHART_DEFAULTS` — its fields are the very same objects as the chart's. Handing them
    // out by reference would let a caller's edit rewrite the factory values for everyone,
    // and tie both tabs' `activity.extension.computed` to a single toggle.
    const defaults = cloneDeep(SECTION_DEFAULTS[section])

    const stored = computed(() => {
        const user = store.state.config.user || {}

        // Users who never opened the "Map settings" tab have no `mapTelemetries` key: the
        // map inherits the chart selection so their popups keep showing exactly what they
        // always showed. `chart` has no such fallback — it is the base selection.
        if (section === 'mapTelemetries') return user.mapTelemetries || user.chart || {}

        return user[section] || {}
    })

    const selection = computed(() =>
        Object.keys(defaults).reduce((merged, field) => {
            // Clone the MERGED field, not just the defaults half: a plain spread copies
            // nested objects by reference, and `activity.extension` — the only nested
            // object here — comes from `stored`, i.e. straight out of Vuex. Without this,
            // editing `selection.activity.extension.computed` would write into the store
            // outside a mutation. Cloning here also keeps `defaults` (this call's private
            // copy, used by resetSettings) safe from the same aliasing.
            merged[field] = cloneDeep({ ...defaults[field], ...(stored.value[field] || {}) })
            return merged
        }, {})
    )

    return { defaults, selection }
}
